import React from 'react';
import { StatePerformanceAggregate, StatePerformanceSnapshot, StatePerformanceStepTiming } from '../api';

const ms = (value?: number) => value == null ? '—' : value >= 1000 ? `${(value / 1000).toFixed(2)} s`
  : value > 0 && value < 0.01 ? '<0.01 ms' : `${value.toFixed(2)} ms`;
const labels: Record<string, string> = { ready: 'Next action ready', waiting: 'Waiting for another player',
  finished: 'Game finished', rejected: 'Rejected', failed: 'Failed', navigation: 'Navigation', interrupted: 'Interrupted' };
const Steps = ({ steps }: { steps: StatePerformanceStepTiming[] }) => <>
  {steps.map((step, index) => <div key={index}>{step.name}: {ms(step.durationMilliseconds)}</div>)}
</>;

function Aggregates({ values }: { values: StatePerformanceAggregate[] }) {
  return <>{values.map(group => <details key={group.classification}>
    <summary>{labels[group.classification] ?? group.classification} · {group.requestCount} samples · P50 {ms(group.totalDuration.p50Milliseconds)} · P95 {ms(group.totalDuration.p95Milliseconds)} · P99 {ms(group.totalDuration.p99Milliseconds)} · Max {ms(group.totalDuration.maximumMilliseconds)}</summary>
    <div className="state-performance-table-wrap"><table className="state-performance-table">
      <thead><tr><th>Step</th><th>Calls</th><th>Average</th><th>P50</th><th>P95</th><th>P99</th><th>Max</th></tr></thead>
      <tbody>{[...group.stages, ...group.details].map((step, index) => <tr key={index}>
        <td>{step.name}</td><td>{step.count}</td><td>{ms(step.averageMilliseconds)}</td>
        <td>{ms(step.p50Milliseconds)}</td><td>{ms(step.p95Milliseconds)}</td><td>{ms(step.p99Milliseconds)}</td><td>{ms(step.maximumMilliseconds)}</td>
      </tr>)}</tbody>
    </table></div>
  </details>)}</>;
}

export function ActionPerformancePanel({ performance }: { performance: NonNullable<StatePerformanceSnapshot['actionPerformance']> }) {
  return <section className="state-performance-section" aria-label="Action performance">
    <h3>Action click to ready</h3>
    <p>From the submitting click or key activation to usable updated controls and the next rendering opportunity (a visibility approximation). Waiting for another player ends when the updated waiting UI is usable; human turn time is excluded. Browser measurements are best effort and reported separately from server time.</p>
    <Aggregates values={performance.clientAggregates} />
    {!performance.clientAggregates.length && <p>No client interactions reported yet.</p>}
    <h3>Recent action interactions</h3>
    {performance.recentInteractions.map(interaction => <details key={interaction.interactionId}>
      <summary>{labels[interaction.outcome]} · {ms(interaction.totalDurationMilliseconds)} · {interaction.inputSource}{interaction.hidden ? ' · Background tab' : ''}</summary>
      <div>Interaction: {interaction.interactionId} · Game: {interaction.gameId ?? 'Unresolved'}</div>
      <Steps steps={interaction.stages} />
      {interaction.attempts.map(attempt => {
        const server = performance.recentServerRequests.find(request => request.dimensions?.requestId === attempt.requestId);
        return <details key={attempt.requestId}>
          <summary>{attempt.kind} attempt · {attempt.statusCode ?? 'No response'} · Headers {ms(attempt.durationMilliseconds)}</summary>
          <div>Request: {attempt.requestId}</div>
          {server ? <><div>Server application total: {ms(server.totalDurationMilliseconds)}</div>
            <Steps steps={[...server.stages, ...server.details]} />
            {Object.entries(server.dimensions ?? {}).map(([key, value]) => <div key={key}>{key}: {value}</div>)}
          </> : <div>No matching server record retained.</div>}
        </details>;
      })}
    </details>)}
    <h3>Action server requests</h3>
    <p>Application time includes middleware, compression and response writes. It excludes upstream proxy time and does not measure browser receipt. Nested stages and shared execution overlap; do not add them to browser durations.</p>
    <Aggregates values={performance.serverAggregates.filter(group => group.classification === 'All')} />
    {performance.recentServerRequests.map((request, index) => <details key={request.dimensions?.requestId ?? index}>
      <summary>{new Date(request.startedAtUtc).toLocaleString()} · {request.outcome} ({request.statusCode ?? 'No status'}) · {ms(request.totalDurationMilliseconds)}</summary>
      <div>Game: {request.gameId ?? 'Unresolved'}</div>
      <Steps steps={[...request.stages, ...request.details]} />
      {Object.entries(request.dimensions ?? {}).map(([key, value]) => <div key={key}>{key}: {value}</div>)}
    </details>)}
  </section>;
}
